import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import Details from './Details';
import { loadModules } from 'esri-loader';
import { streamApi } from '../../functions/streamApi';

// const USA_CENTER = [-95.7129, 37.0902];
const USA_PROVIDENCE = [-71.4128, 41.824];
const USA_WOBURN = [-71.15539929250293, 42.496872539511145];

export const statusColors = {
  active: '#ef4444',
  open: '#FFF89A',
  closed: '#22c55e',
  reviewed: '#22a5c5',
};

export const statusColorsOutline = {
  active: 'rgba(184, 17, 17, 0.411)',
  open: 'rgba(192, 154, 0, 0.349)',
  closed: 'rgba(0, 156, 57, 0.506)',
  reviewed: 'rgba(0, 112, 156, 0.506)',
};

const MapComponent = ({ dateRange, searchQuery }) => {
  const mapRef = useRef(null);
  const graphicsRef = useRef([]);
  const objectIdCounter = useRef(1);
  const featureLayerRef = useRef(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [loadingPoints, setLoadingPoints] = useState();

  const searchQueryLast = useRef(searchQuery);

  const department = localStorage.getItem('department');

  useEffect(() => {
    let viewLocal;
    let streamHandle;
    let isCancelled = false;
    const buffer = [];
    let bufferTimer = null;

    if (searchQueryLast.current !== searchQuery) {
      setLoadingPoints(true);
      searchQueryLast.current = searchQuery;
    }

    const flushBuffer = () => {
      if (buffer.length === 0 || !featureLayerRef.current) return;
      const featuresToAdd = buffer.splice(0, buffer.length);
      featureLayerRef.current
        .applyEdits({ addFeatures: featuresToAdd })
        .catch((err) => console.error('applyEdits error:', err));
    };

    loadModules(
      [
        'esri/Map',
        'esri/views/MapView',
        'esri/layers/FeatureLayer',
        'esri/Graphic',
      ],
      { css: true }
    ).then(([EsriMap, MapView, FeatureLayer, Graphic]) => {
      const map = new EsriMap({ basemap: 'osm' });

      viewLocal = new MapView({
        container: mapRef.current,
        map,
        center: department === 'ma17347' ? USA_WOBURN : USA_PROVIDENCE,
        zoom: 11,
      });

      const clusterConfig = {
        type: 'cluster',
        clusterRadius: '100px',
        clusterMinSize: '24px',
        clusterMaxSize: '60px',
        clusterAreaDisplay: 'expand',
        // popupTemplate: {
        //   title: 'Кластер інцидентів',
        //   content: 'Кількість інцидентів: {cluster_count}',
        // },
        labelingInfo: [
          {
            deconflictionStrategy: 'none',
            labelExpressionInfo: {
              expression: "Text($feature.cluster_count, '#,###')",
            },
            symbol: {
              type: 'text',
              color: '#3f3f3f',
              font: {
                weight: 'bold',
                family: 'Noto Sans',
                size: '12px',
              },
            },
            labelPlacement: 'center-center',
          },
        ],
      };

      const featureLayer = new FeatureLayer({
        objectIdField: 'ObjectID',
        geometryType: 'point',
        outFields: ['*'],
        fields: [
          { name: 'ObjectID', type: 'oid' },
          { name: 'status', type: 'string' },
          { name: 'incidentId', type: 'string' },
          { name: 'incident_number', type: 'string' },
          { name: 'dispatch_time', type: 'string' },
          { name: 'incident_types', type: 'string' },
          { name: 'address', type: 'string' },
          { name: 'first_arriving_unit', type: 'string' },
          { name: 'responded_units', type: 'string' },
          { name: 'neris_status', type: 'string' },
        ],
        source: [],
        featureReduction: clusterConfig,
        renderer: {
          type: 'unique-value',
          field: 'status',
          uniqueValueInfos: Object.entries(statusColors).map(
            ([status, color]) => ({
              value: status,
              symbol: {
                type: 'simple-marker',
                style: 'circle',
                color,
                size: '18px',
                outline: {
                  color: statusColorsOutline[status],
                  width: 5,
                },
              },
            })
          ),
          defaultSymbol: {
            type: 'simple-marker',
            style: 'circle',
            color: '#9ca3af',
            size: '18px',
            outline: {
              color: 'rgba(145, 145, 145, 0.506)',
              width: 5,
            },
          },
        },
      });

      // Cleaning the old layer (if any)
      if (featureLayerRef.current) {
        map.remove(featureLayerRef.current);
        featureLayerRef.current.destroy();
      }

      map.add(featureLayer);
      featureLayerRef.current = featureLayer;

      viewLocal.on('click', async (event) => {
        const response = await viewLocal.hitTest(event);
        const graphic = response.results.find(
          (r) => r.graphic.layer === featureLayer
        )?.graphic;

        if (!graphic) {
          setSelectedIncident(null);
          // viewLocal.popup.close();
          return;
        }

        // If this is a cluster
        if (graphic.attributes?.aggregateId) {
          if (viewLocal.zoom >= 18) {
            const aggregateId = graphic.attributes.aggregateId;

            const { features } = await featureLayerRef.current.queryFeatures({
              where: '1=1',
              returnGeometry: true,
              outFields: ['*'],
              aggregateIds: [aggregateId],
            });

            const center = graphic.geometry;
            const threshold = 0.00006; // ~1m

            const clusterMembers = features.filter((g) => {
              const dx = Math.abs(g.geometry.longitude - center.longitude);
              const dy = Math.abs(g.geometry.latitude - center.latitude);
              return dx < threshold && dy < threshold;
            });

            viewLocal.popup.open({
              location: graphic.geometry,
              title: `Incidents in the cluster: ${clusterMembers.length}`,
              content: document.createElement('div'),
            });

            const container = viewLocal.popup.content;
            // container.style.maxHeight = '300px';
            // container.style.overflowY = 'auto';
            container.innerHTML = `
              <style>
              
                .cluster-item {
                  margin-bottom: 5px;
                  cursor: pointer;
                  color: #242424;
                  background: #F3F3F3;
                  padding: 5px;
                  border-radius: 5px;
                  transition: background 0.2s;
                }
                .cluster-item:hover {
                  background: #e0e0e0;
                }
              </style>
              <ul style="list-style:none;padding:0;margin:0; ">
                ${clusterMembers
                  .map(
                    (incident, idx) => `
                  <li class="cluster-item" data-index="${idx}">
                    ${incident.attributes.incident_number || 'N/A'} – ${
                      incident.attributes.status
                    }
                  </li>`
                  )
                  .join('')}
              </ul>
            `;

            // incident click handler in the popup
            container.querySelectorAll('li').forEach((li) => {
              li.addEventListener('click', () => {
                const index = li.getAttribute('data-index');
                const incident = clusterMembers[index];

                // viewLocal.popup.close();

                setSelectedIncident({
                  incident_id: incident.attributes.incidentId,
                  incident_number: incident.attributes.incident_number,
                  status: incident.attributes.status,
                  coordinates: [
                    incident.geometry.latitude,
                    incident.geometry.longitude,
                  ],
                  incident_types: incident.attributes.incident_types,
                  address: incident.attributes.address,
                  dispatch_time: incident.attributes.dispatch_time,
                  first_arriving_unit: incident.attributes.first_arriving_unit,
                  responded_units: incident.attributes.responded_units,
                  neris_status: incident.attributes.neris_status,
                });

                // Optionally, center on a point
                viewLocal.goTo(incident.geometry);
              });
            });
          }
        } else if (graphic && graphic.attributes && !graphic.isAggregate) {
          setSelectedIncident({
            incident_id: graphic.attributes.incidentId,
            incident_number: graphic.attributes.incident_number,
            status: graphic.attributes.status,
            coordinates: [
              graphic.geometry.latitude,
              graphic.geometry.longitude,
            ],
            incident_types: graphic.attributes.incident_types,
            address: graphic.attributes.address,
            dispatch_time: graphic.attributes.dispatch_time,
            first_arriving_unit: graphic.attributes.first_arriving_unit,
            responded_units: graphic.attributes.responded_units,
            neris_status: graphic.attributes.neris_status,
          });
        } else {
          setSelectedIncident(null);
        }
      });

      if (graphicsRef.current.length === 0) {
        setLoadingPoints(true);
      }

      // if (graphicsRef.current.length > 500) {
      //   graphicsRef.current.shift();
      // }

      streamHandle = streamApi.streamIncidentsCoordinates(
        dateRange,
        searchQuery,
        () => {
          setLoadingPoints(true);
        },
        () => {
          setLoadingPoints(false);
        },
        (incident) => {
          if (isCancelled) return;

          const [lat, lng] = incident.geometry_coordinates;

          const graphic = new Graphic({
            geometry: { type: 'point', latitude: lat, longitude: lng },
            attributes: {
              ObjectID: objectIdCounter.current++,
              status: incident.status,
              incidentId: incident.incident_id,
              incident_number: incident.incident_number,
              dispatch_time: incident.dispatch_time,
              incident_types: incident.incident_types,
              address: incident.address,
              first_arriving_unit: incident.first_arriving_unit,
              responded_units: incident.responded_units,
              neris_status: incident.neris_status,
            },
            symbol: {
              type: 'simple-marker',
              style: 'circle',
              color: statusColors[incident.status] || '#e40000',
              size: '18px',
              outline: {
                color:
                  statusColorsOutline[incident.status] || 'rgba(0,0,0,0.2)',
                width: 5,
              },
            },
          });

          graphicsRef.current.push({ graphic: graphic, incident });

          buffer.push(graphic);

          if (!bufferTimer) {
            bufferTimer = setTimeout(() => {
              flushBuffer();
              bufferTimer = null;
            }, 300); // 300ms буферизація
          }
          // featureLayer
          //   .applyEdits({ addFeatures: [graphic] })
          //   .catch((error) => console.error('applyEdits error:', error));
        },
        () => {
          flushBuffer();
          console.log('Stream ended');
        }
      );
    });

    return () => {
      isCancelled = true;
      if (streamHandle?.abort) streamHandle.abort();
      flushBuffer();
      if (bufferTimer) clearTimeout(bufferTimer);

      if (viewLocal) {
        viewLocal.container = null;
        viewLocal.destroy();
      }
    };
  }, [dateRange, searchQuery]);

  return (
    <Box
      sx={{ width: '100%', height: 'calc(100% - 57px)', position: 'relative' }}
    >
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      <Details
        incident={selectedIncident}
        onClose={() => setSelectedIncident(null)}
      />
      {loadingPoints && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            background: '#ffffff81',
          }}
        >
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MapComponent;
